// IE 2026 09 24 / ODOO-341407 (Igor: «переводить всё, что видит пользователь» — компактные длительности «5m 19d», «1.1y», «1m» оставались
// английскими под uk/ru): единица длительности под язык интерфейса.
//   en — как было: число и буква слитно, десятичная точка («1.1y», «5m 19d», «2.5h», «38ms»);
//   uk / ru — шаблон словаря ('{n}y' → '{n} р' / '{n} г', '{n}m' → '{n} міс' / '{n} мес', '{n}d' → '{n} дн', '{n}h' → '{n} год' / '{n} ч',
//   '{n}min' → '{n} хв' / '{n} мин', '{n}sec' → '{n} с' / '{n} сек', '{n}ms' → '{n} мс'), десятичная запятая, неразрывный пробел между
//   числом и единицей («1,1 р», «5 міс 19 дн», «1 міс»).
// ТОЛЬКО для показа. Текст, который потом разбирается (буфер обмена → parseDays / parseTime при вставке, ввод составной длительности
// в форме ячейки), — канонический английский: durUnit(n, unit, false) / formatDaysHTML(…, { localize: false }). Сортировка, ключи и
// значения API — сырые числа, их форматтер не касается.
// Живое переключение языка: t() читает langVersion — шаблоны Vue перерисовываются сами; грид (invalidate на 'i18n:changed'), оси и
// подсказки чартов зовут форматтер при отрисовке.
import { t, getLanguage, LANGUAGE_DEFAULT } from '@/i18n'

// литеральные ключи — их видит сверка словаря (tools/i18n_extract_fe.py)
const UNIT = {
    y: (n) => t('{n}y', { n }),
    m: (n) => t('{n}m', { n }),
    d: (n) => t('{n}d', { n }),
    h: (n) => t('{n}h', { n }),
    min: (n) => t('{n}min', { n }),
    sec: (n) => t('{n}sec', { n }),
    ms: (n) => t('{n}ms', { n }),
}

// показывать ли единицы локально (язык интерфейса не английский)
export function durLocalized() { return getLanguage() !== LANGUAGE_DEFAULT }

// n — число или его готовый текст ('1.1', '-5', '19'); unit — 'y' | 'm' | 'd' | 'h' | 'min' | 'sec' | 'ms';
// localize === false — канон «1.1y» (для разбора), как и под en
export function durUnit(n, unit, localize = true) {
    const s = String(n)
    if (!localize || !UNIT[unit] || getLanguage() === LANGUAGE_DEFAULT) return s + unit
    return UNIT[unit](s.replace('.', ',')).replace(/ /g, '\u00a0')
}

// число длительности без единицы — для подписей, которые идут шаблоном словаря целиком («5.6 m (169 d)» → «5,6 міс (169 дн)»):
// uk / ru — десятичная запятая; en / localize === false — как есть
export function durNum(n, localize = true) {
    const s = String(n)
    return (!localize || getLanguage() === LANGUAGE_DEFAULT) ? s : s.replace('.', ',')
}

// IE 2026 09 29 / ODOO-341407: форматтер длительности AttrTime (секунды) — тело store.formatTimeHTML, перенесённое сюда без изменений,
// чтобы его проверял юнит-тест без сборки (tests/unit/timeFormatDays.test.mjs); store.formatTimeHTML — обёртка над ним.
// Новое — формат 'd' («в днях», пакет Demo №15c: срок поставки 17q_001, ось DOS 0dU_001): всегда дни — от 10 дней целые
// («80d», uk «80 дн»), меньше — одна десятичная («5d», «0.3d»), ноль и не-число — «0d»; ветка малых значений (< 3 с) к 'd' не
// применяется, как и к 'm'. Формат 'm' и автоединицы — как были.
export function formatTimeText(
    seconds,
    attr_col,
    {
        daysInMonth = 30,
        precision = 1,
        trimZeros = true,
        _msDone = false,
        localize = true   // IE 2026 09 24 / ODOO-341407: false — канонический английский текст («14m») для разбора (буфер обмена → parseTime)
    } = {}
) {
    const U = (n, u) => durUnit(n, u, localize)   // IE 2026 09 24 / ODOO-341407: единица под язык интерфейса (en — как было: «2.5h», «14m»)
    const days_on = attr_col?.format === 'd'   // IE 2026 09 29 / ODOO-341407: формат «в днях»
    seconds = Number(seconds);
    // 'unit': 'ms' на атрибуте — сырое значение в миллисекундах (напр. test
    // recording t_ms/dur_ms): конвертируем один раз в секундах и форматируем как обычно
    if (attr_col?.unit === 'ms' && !_msDone) {
        seconds = seconds / 1000;
    }
    // sentinel ±999 месяцев (999*30*24*60*60 секунд)
    if (Math.abs(seconds) === 999 * 30 * 24 * 3600) {
        return seconds < 0 ? '-∞' : '∞';
    }
    if (seconds < 0) {
        return '-' + formatTimeText(-seconds, attr_col, { daysInMonth, precision, trimZeros, _msDone: true, localize });   // IE 2026 09 24 / ODOO-341407
    }
    if (!Number.isFinite(seconds)) {
        // IE 2026 09 10 / ODOO-5800: при формате 'm' — «0m», как formatDaysHTML (DOS переехал в секунды)
        if (days_on) return U(0, 'd')   // IE 2026 09 29 / ODOO-341407: формат 'd' — «0d»
        return attr_col?.format === 'm' ? U(0, 'm') : U(0, 'sec');   // IE 2026 09 24 / ODOO-341407
    }
    // малые значения (0 < sec < 3): не схлопываем в '0 sec' — показываем
    // с реальной точностью (38 ms / 1.2 sec). Значения ≥3 sec — как раньше.
    if (seconds > 0 && seconds < 3 && !(attr_col?.format === 'm') && !days_on) {   // IE 2026 09 29 / ODOO-341407: и не при 'd'
        if (seconds < 1) {
            return U(Math.round(seconds * 1000), 'ms');   // IE 2026 09 24 / ODOO-341407
        }
        return U(+seconds.toFixed(1), 'sec');
    }

    const SEC_IN_DAY   = 24 * 3600;
    const SEC_IN_MONTH = daysInMonth * SEC_IN_DAY;

    const formatNum = (n) => {
        const v = n.toFixed(precision);
        return trimZeros ? v.replace(/\.?0+$/, '') : v;
    };

    // IE 2026 09 29 / ODOO-341407: формат 'd' — всегда дни: от 10 дней целые («80d»), меньше — одна десятичная («5d», «0.3d»), ноль — «0d»
    if (days_on) {
        if (seconds === 0) return U(0, 'd')
        const days = seconds / SEC_IN_DAY
        return days >= 10 ? U(Math.round(days), 'd') : U(formatNum(days), 'd')
    }

    // 👉 Формат = месяцы (дробные; после 3 месяцев дробная часть
    // не информативна — округляем до целых: 7.1m → 7m)
    if (attr_col?.format === 'm') {
        const months = seconds / SEC_IN_MONTH;
        if (seconds === 0) {
            return U(0, 'm'); // IE 2026 09 10 / ODOO-5800: ноль — «0m», как formatDaysHTML; IE 2026 09 24 / ODOO-341407: единица под язык
        }
        if (months > 3) {
            return U(Math.round(months), 'm');
        }
        if (months < 1) {
            // меньше месяца — целые дни (0.3m нечитабельно)
            return U(Math.round(seconds / SEC_IN_DAY), 'd');
        }
        return U(formatNum(months), 'm');
    }

    // 👉 Обычный формат: одна доминирующая единица (m / d / h / min / sec).
    // Составные цепочки («1m 18d 16h 5min») нечитабельны и режутся в узких
    // колонках; >3 единиц дробная часть не информативна — целые (как в 'm')
    const months = seconds / SEC_IN_MONTH;
    // IE 2026 09 24 / ODOO-341407: единицы — U() (под язык интерфейса; en — как было)
    if (months >= 1) {
        return months > 3 ? U(Math.round(months), 'm') : U(formatNum(months), 'm');
    }
    const daysV = seconds / SEC_IN_DAY;
    if (daysV >= 1) {
        return daysV > 3 ? U(Math.round(daysV), 'd') : U(formatNum(daysV), 'd');
    }
    const hoursV = seconds / 3600;
    if (hoursV >= 1) {
        return U(formatNum(hoursV), 'h');
    }
    const minutesV = seconds / 60;
    if (minutesV >= 1) {
        return U(Math.round(minutesV), 'min');
    }
    return U(Math.round(seconds), 'sec');
}

// IE 2026 09 29 / ODOO-341407: шаг оси длительности в формате 'd' (секунды) — кратен суткам и подобран по размаху оси: наименьший из
// лестницы 1 / 2 / 5 / 7 / 14 / 30 / 60 / 90 / 180 / 365 дн (дальше — годы по 1-2-5: 730, 1 825, 3 650, 7 300 …), при котором меток
// не больше maxLabels. Постоянный шаг не годится: DOS на Distribution — 5–21 дн, на Schedules — ~4 400 дн.
export const TIME_AXIS_DAYS_STEPS = [1, 2, 5, 7, 14, 30, 60, 90, 180, 365]
export function timeAxisDaysStep(rangeSec, maxLabels = 5) {
    const DAY = 24 * 3600
    const days = (Number.isFinite(rangeSec) && rangeSec > 0) ? rangeSec / DAY : 0
    const n = Math.max(1, Math.floor(maxLabels) || 1)
    for (const d of TIME_AXIS_DAYS_STEPS) if (days / d <= n) return d * DAY
    for (let k = 1; k < 1e12; k *= 10) {
        for (const m of [2, 5, 10]) { const d = 365 * k * m; if (days / d <= n) return d * DAY }
    }
    return 365 * 1e12 * DAY   // размах вне разумного — один шаг на всю ось
}
